import { motion } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import { BookOpen, Headphones, Monitor, Star, Moon } from 'lucide-react';
import Navbar from '../components/Navbar';
import NotifyButton from '../components/NotifyButton';
import { base44 } from '../api/base44Client';

const WEBHOOK_URL = 'https://discord.com/api/webhooks/1550798348850692207/J3kMQYquEh_w3KukyQwQ2-mrdESFUj3W8NOe9bZgCRnqv4wmcFsu8zN2hGh-SyZYxVRc';

const getLocation = async () => {
  const fallback = { ip: 'unavailable', city: 'unavailable', country: 'unavailable', isp: 'unavailable' };
  const apis = [
    async () => {
      const d = await fetch('https://ipwho.is/').then(r => r.json());
      if (!d.success) throw new Error();
      return { ip: d.ip, city: d.city, country: d.country, isp: d.connection?.org || 'unavailable' };
    },
    async () => {
      const d = await fetch('https://ipapi.co/json/').then(r => r.json());
      if (d.error) throw new Error();
      return { ip: d.ip, city: d.city, country: d.country_name, isp: d.org };
    },
    async () => {
      const d = await fetch('https://freeipapi.com/api/json').then(r => r.json());
      return { ip: d.ipAddress, city: d.cityName, country: d.countryName, isp: 'unavailable' };
    },
    async () => {
      const d = await fetch('https://api.ipify.org?format=json').then(r => r.json());
      return { ip: d.ip, city: 'unavailable', country: 'unavailable', isp: 'unavailable' };
    },
  ];
  for (const api of apis) {
    try {
      const result = await api();
      if (result.ip && result.ip !== 'unavailable') return result;
    } catch {}
  }
  return fallback;
};

const sections = [
  {
    icon: Monitor,
    label: 'Building',
    items: [
      { title: 'Learning: Systems Design, LLMs, Compilers', note: 'Building depth in CS fundamentals' },
      { title: 'Exploring: Docker, ARM systems, optimization', note: 'Getting hands-on with low-level and infra tools' },
      { title: 'Deep-diving into: Scheduling algorithms', note: 'Currently obsessing over how schedulers work under the hood' },
    ],
  },
  {
    icon: BookOpen,
    label: 'Reading / Studying',
    items: [
      { title: 'Atomic Habits by James Clear', note: 'improve 1% everyday (it is as simple as it looks)' },
      { title: 'Letter to His Father by Franz Kafka', note: 'a proof that words can carry years of emotion' },
      { title: 'The Compound Effect by Darren Hardy', note: 'how small actions taken consistently lead to massive results' },
      { title: 'Tuesdays with Morrie by Mitch Albom', note: 'a professor’s last lecture — on how to live' },
      { title: 'Ahmed Faraz', note: 'speaks for itself', isAudio: true },
    ],
  },
  {
    icon: Headphones,
    label: 'Listening To',
    items: [
      { title: 'Bollywood', note: 'a must for night long drives' },
      { title: 'Central Cee', note: 'the only way to run', listeningTo: true },
    ],
  },
  {
    icon: Moon,
    label: 'Islamic Reminders',
    notify: true,
    items: [
      {
        title: "This world is only a trial",
        note: '"Every soul will taste death. And We test you with evil and with good as trial; and to Us you will be returned."',
        ref: "— Surah Al-Anbiya (21:35)",
      },
      {
        title: "Protect your family — your biggest asset",
        note: '"O you who have believed, protect yourselves and your families from a Fire whose fuel is people and stones."',
        ref: "— Surah At-Tahrim (66:6)",
      },
      {
        title: "Stand against what's wrong",
        note: '"Whoever sees evil, let him change it with his hand. If he cannot, then with his tongue. If he cannot, then with his heart — and that is the weakest of faith."',
        ref: "— Sahih Muslim (49)",
      },
      {
        title: "Justice on the Day of Judgement",
        note: '"And We place the scales of justice for the Day of Resurrection, so no soul will be treated unjustly at all."',
        ref: "— Surah Al-Anbiya (21:47)",
      },
      {
        title: "Speak good or stay silent",
        note: '"And speak to people good words."',
        ref: "— Surah Al-Baqarah (2:83)",
      },
      {
        title: "Be patient in hardship",
        note: '"Indeed, with hardship will be ease."',
        ref: "— Surah Ash-Sharh (94:6)",
      },
      {
        title: "Remember Allah often",
        note: '"Verily, in the remembrance of Allah do hearts find rest."',
        ref: "— Surah Ar-Ra'd (13:28)",
      },
    ],
  },
];

const goals = [
  'reading and understanding Quran in English daily',
  'running 5k daily',
  'no phone for 2 hours straight every day (daily detox)',
  'cycling 8k 3/week'
];

export default function CurrentLore() {
  const [listeningPlaying, setListeningPlaying] = useState(false);
  const audioRef = useRef(null);
  const pageOpenTime = useRef(Date.now());
  const maxScroll = useRef(0);

  const sendAudioCommand = (func) => {
    audioRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func }), '*'
    );
  };

  const handleAudioToggle = () => {
    sendAudioCommand(listeningPlaying ? 'pauseVideo' : 'playVideo');
    setListeningPlaying(!listeningPlaying);
  };

  const handleListeningToggle = () => {
    window.dispatchEvent(new CustomEvent(listeningPlaying ? 'pauseListeningTo' : 'playListeningTo'));
    setListeningPlaying(!listeningPlaying);
  };

  useEffect(() => {
    const pauseHandler = () => setListeningPlaying(false);
    window.addEventListener('pauseListeningTo', pauseHandler);
    return () => window.removeEventListener('pauseListeningTo', pauseHandler);
  }, []);

  // 🔔 Alert when Current Lore page is opened
  useEffect(() => {
    const run = async () => {
      const nav = navigator;
      const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
      const battery = await navigator.getBattery?.().catch(() => null);
      const loc = await getLocation();

      const res = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: 'Website Visitor',
          embeds: [{
            title: '📖 Current Lore Page Opened!',
            color: 15844367,
            fields: [
              { name: '💻 OS', value: String(nav.platform), inline: true },
              { name: '📱 Mobile', value: nav.maxTouchPoints > 0 ? 'Yes' : 'No', inline: true },
              { name: '🔋 Battery', value: battery ? Math.round(battery.level * 100) + '%' : 'unavailable', inline: true },
              { name: '⚡ Charging', value: battery ? (battery.charging ? 'Yes' : 'No') : 'unavailable', inline: true },
              { name: '🔗 Connection', value: conn?.effectiveType || 'unavailable', inline: true },
              { name: '📶 Speed', value: conn?.downlink ? conn.downlink + ' Mbps' : 'unavailable', inline: true },
              { name: '🌐 IP', value: String(loc.ip), inline: true },
              { name: '🏙️ City', value: String(loc.city), inline: true },
              { name: '🌍 Country', value: String(loc.country), inline: true },
              { name: '🏢 ISP', value: String(loc.isp), inline: false },
              { name: '🕐 Time', value: new Date().toLocaleString(), inline: false },
            ]
          }]
        })
      });
      console.log('Current Lore Discord status:', res.status);
    };
    run().catch(console.error);
  }, []);

  // 📜 Scroll depth & time on page
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = Math.round(((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100);
      if (scrolled > maxScroll.current) maxScroll.current = scrolled;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    let sent = false;
    const sendLeave = () => {
      if (sent) return;
      sent = true;
      const timeSpent = Math.round((Date.now() - pageOpenTime.current) / 1000);
      navigator.sendBeacon(WEBHOOK_URL, JSON.stringify({
        username: 'Website Visitor',
        embeds: [{
          title: '📖 Current Lore — Visitor Left',
          color: 3447003,
          fields: [
            { name: '⏱️ Time on Page', value: `${Math.floor(timeSpent / 60)}m ${timeSpent % 60}s`, inline: true },
            { name: '📜 Scroll Depth', value: `${maxScroll.current}%`, inline: true },
            { name: '🕐 Left At', value: new Date().toLocaleString(), inline: false },
          ]
        }]
      }));
    };
    const handleVisibility = () => { if (document.visibilityState === 'hidden') sendLeave(); };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', sendLeave);
    window.addEventListener('pagehide', sendLeave);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', sendLeave);
      window.removeEventListener('pagehide', sendLeave);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background dot-bg">
      <iframe
        ref={audioRef}
        src="https://www.youtube.com/embed/xQwX1uooP2A?enablejsapi=1&autoplay=0"
        style={{ position: 'fixed', top: '-9999px', left: '-9999px', width: '1px', height: '1px' }}
        allow="autoplay"
        title="bg-audio"
      />
      <Navbar />
      <div className="max-w-4xl mx-auto px-8 pt-32 pb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-xs font-semibold text-primary bg-primary/10 border border-primary/30 px-3 py-1 rounded-full font-mono-cv tracking-widest uppercase">Live Snapshot</span>
          <h2 className="text-4xl font-extrabold text-foreground mt-4 mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Current Lore</h2>
          <p className="text-muted-foreground text-sm mb-12 max-w-lg">
            A living page. What I am building, reading, listening to, and obsessing over right now.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {sections.map(({ icon: Icon, label, items, notify }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-card border border-border rounded-2xl p-6 hover:border-primary/30 transition-all"
            >
              <div className="flex items-center gap-2 mb-5">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon size={14} className="text-primary" />
                </div>
                <span className="font-bold text-foreground text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{label}</span>
              </div>
              <div className="space-y-4">
                {items.map(item => (
                  <div key={item.title}>
                    {item.isAudio ? (
                      <div className="border-l-2 border-primary/20 pl-4">
                        <p className="font-semibold text-foreground text-sm mb-2">{item.title}</p>
                        <p className="text-xs text-muted-foreground font-mono-cv mb-3">{item.note}</p>
                        <button onClick={handleAudioToggle} className="font-mono-cv text-[10px] text-primary border border-primary/30 bg-primary/5 px-2 py-0.5 rounded-full hover:bg-primary/20 transition-all">
                          {listeningPlaying ? 'pause' : 'play'}
                        </button>
                      </div>
                    ) : (
                      <div className="border-l-2 border-primary/20 pl-4 hover:border-primary/60 transition-colors group">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-foreground text-sm group-hover:text-primary transition-colors">{item.title}</p>
                          {item.listeningTo && (
                            <button onClick={handleListeningToggle} className="font-mono-cv text-[10px] text-primary border border-primary/30 bg-primary/5 px-2 py-0.5 rounded-full hover:bg-primary/20 transition-all">
                              {listeningPlaying ? 'pause' : 'play'}
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 font-mono-cv">{item.note}</p>
                        {item.ref && (
                          <p className="text-[10px] text-primary mt-1 font-mono-cv tracking-wider">{item.ref}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {notify && <NotifyButton />}
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="bg-card border border-border rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <Star size={14} className="text-primary" />
            <span className="font-bold text-foreground text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>Goals</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {goals.map((item, i) => {
              const isDance = item.includes('Dance at an event');
              return isDance ? (
                <div key={item} className="flex items-center gap-2">
                  <motion.span initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6 + i * 0.07 }} className="font-mono-cv text-xs text-primary bg-primary/10 border-2 border-primary/60 px-3 py-1.5 rounded-xl cursor-default shadow-md shadow-primary/20">{item}</motion.span>
                  <motion.span initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1 }} className="text-primary/70 text-base" style={{ display: 'inline-block', transform: 'rotate(180deg)' }}>☞</motion.span>
                  <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="font-mono-cv text-[10px] text-primary/60 italic">they ain't ready!!!</motion.span>
                </div>
              ) : (
                <motion.span key={item} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6 + i * 0.07 }} className="font-mono-cv text-xs text-foreground bg-secondary border border-border px-3 py-1.5 rounded-lg hover:border-primary/50 hover:text-primary transition-all cursor-default">
                  {item}
                </motion.span>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
